/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Tab,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';
import {
  FaEdit,
  FaSave,
  FaTimes,
  FaUser,
  FaTicketAlt,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaRobot,
} from 'react-icons/fa';
import DataTable from 'react-data-table-component';
import api from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import TagBadge from '../../components/TagSelector/TagBadge';
import TagSelector from '../../components/TagSelector';

const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
  }
  return phone;
};

function CustomerEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [assets, setAssets] = useState([]);
  const [assetSearchText, setAssetSearchText] = useState('');
  const [debouncedAssetSearchText, setDebouncedAssetSearchText] = useState('');

  // Estados para as abas
  const [requesters, setRequesters] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [requestersLoading, setRequestersLoading] = useState(false);
  const [editingRequester, setEditingRequester] = useState(null);
  const [requesterEditData, setRequesterEditData] = useState({});
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para Mesa de Serviço
  const [allServiceDesks, setAllServiceDesks] = useState([]);
  const [serviceDeskIds, setServiceDeskIds] = useState([]);
  const [serviceDeskLoading, setServiceDeskLoading] = useState(false);

  // Estados para Etiquetas
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Estados para Relatórios Mensais com IA
  const [insightsAtivo, setInsightsAtivo] = useState(false);
  const [insightsConfig, setInsightsConfig] = useState({
    produtos: [],
    telefones: [''],
    servidor: '',
    tokenChat: '',
    tokenTelefonia: '',
  });
  const [insightsLoading, setInsightsLoading] = useState(false);

  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/customers/${id}`);

      // Aplica formatação ao CPF/CNPJ baseado no tipo de pessoa
      const formattedData = { ...data };
      if (data.cpfCnpj) {
        if (data.type_person === 'fisica') {
          formattedData.cpfCnpj = data.cpfCnpj.replace(
            /(\d{3})(\d{3})(\d{3})(\d{2})/g,
            '$1.$2.$3-$4'
          );
        } else if (data.type_person === 'juridica') {
          formattedData.cpfCnpj = data.cpfCnpj
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
        }
      }

      setCustomer(formattedData);
      setEditValues(formattedData);

      // Carregar mesas de serviço associadas
      if (data.serviceDesks) {
        const linkedIds = data.serviceDesks.map((desk) => desk.id);
        setServiceDeskIds(linkedIds);
      }

      // Carregar etiquetas associadas
      if (data.tags) {
        setSelectedTags(data.tags);
      }

      // Carregar configuração de insights
      setInsightsAtivo(data.insightsAtivo || false);
      setInsightsConfig({
        produtos: data.insightsProdutos || [],
        telefones: data.insightsTelefones?.length
          ? data.insightsTelefones
          : [''],
        servidor: data.insightsServidor || '',
        tokenChat: data.insightsTokenChat || '',
        tokenTelefonia: data.insightsTokenTelefonia || '',
      });
    } catch (err) {
      setError('Erro ao carregar dados do cliente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAssets = useCallback(async () => {
    try {
      const { data } = await api.get(`/assets/customer/${id}`);
      setAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      setAssets([]);
    }
  }, [id]);

  const fetchRequesters = useCallback(async () => {
    try {
      setRequestersLoading(true);
      const { data } = await api.get(`/requesters/company/${id}`);
      setRequesters(data || []);
    } catch (err) {
      console.error('Erro ao carregar solicitantes:', err);
      setRequesters([]);
    } finally {
      setRequestersLoading(false);
    }
  }, [id]);

  const fetchAllServiceDesks = useCallback(async () => {
    try {
      setServiceDeskLoading(true);
      const { data } = await api.get('/servicedesks');
      setAllServiceDesks(data);
    } catch (err) {
      console.error('Erro ao carregar Mesas de Serviço:', err);
      setError('Erro ao carregar Mesas de Serviço');
    } finally {
      setServiceDeskLoading(false);
    }
  }, []);

  const fetchAvailableTags = useCallback(async () => {
    try {
      setTagsLoading(true);
      const { data } = await api.get('/tags');
      setAvailableTags(data);
    } catch (err) {
      console.error('Erro ao carregar etiquetas:', err);
      setError('Erro ao carregar etiquetas');
    } finally {
      setTagsLoading(false);
    }
  }, []);

  const handleServiceDeskChange = (deskId) => {
    setServiceDeskIds((prevIds) => {
      if (prevIds.includes(deskId)) {
        return prevIds.filter((x) => x !== deskId);
      }
      return [...prevIds, deskId];
    });
  };

  const saveServiceDeskAssociations = async () => {
    try {
      setServiceDeskLoading(true);
      await api.put(`/customers/${id}`, {
        serviceDeskIds,
      });
      setSuccess('Associações de Mesa de Serviço atualizadas com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao atualizar associações de Mesa de Serviço');
      console.error(err);
    } finally {
      setServiceDeskLoading(false);
    }
  };

  const saveTagAssociations = async () => {
    try {
      setTagsLoading(true);
      const tagIds = selectedTags.map((tag) => tag.id);
      await api.put(`/customers/${id}`, {
        tagIds,
      });
      setSuccess('Etiquetas atualizadas com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao atualizar etiquetas');
      console.error(err);
    } finally {
      setTagsLoading(false);
    }
  };

  const saveInsightsConfig = async () => {
    try {
      setInsightsLoading(true);
      await api.put(`/customers/${id}`, {
        insightsAtivo,
        insightsProdutos: insightsAtivo ? insightsConfig.produtos : [],
        insightsTelefones: insightsAtivo
          ? insightsConfig.telefones.filter((t) => t.trim())
          : [],
        insightsServidor: insightsAtivo ? insightsConfig.servidor : '',
        insightsTokenChat: insightsAtivo ? insightsConfig.tokenChat : '',
        insightsTokenTelefonia: insightsAtivo
          ? insightsConfig.tokenTelefonia
          : '',
      });
      setSuccess('Configuração de Relatórios com IA salva com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar configuração de relatórios com IA');
      console.error(err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleInsightsProdutoChange = (produto) => {
    setInsightsConfig((prev) => {
      const produtos = prev.produtos.includes(produto)
        ? prev.produtos.filter((p) => p !== produto)
        : [...prev.produtos, produto];
      return { ...prev, produtos };
    });
  };

  const handleTelefoneChange = (index, value) => {
    setInsightsConfig((prev) => {
      const telefones = [...prev.telefones];
      telefones[index] = value;
      return { ...prev, telefones };
    });
  };

  const addTelefone = () => {
    setInsightsConfig((prev) => ({
      ...prev,
      telefones: [...prev.telefones, ''],
    }));
  };

  const removeTelefone = (index) => {
    setInsightsConfig((prev) => ({
      ...prev,
      telefones: prev.telefones.filter((_, i) => i !== index),
    }));
  };

  const fetchTickets = useCallback(async () => {
    try {
      setTicketsLoading(true);
      const params = {
        company: id,
        page: ticketPage,
        limit: 50,
        ...(ticketSearch && { search: ticketSearch }),
      };

      const { data } = await api.get('/tickets', { params });
      setTickets(data.tickets || data || []);
      setTicketTotalPages(Math.ceil((data.totalCount || data.length) / 50));
    } catch (err) {
      console.error('Erro ao carregar tickets:', err);
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  }, [id, ticketPage, ticketSearch]);

  // Debounce para pesquisa de tickets
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'tickets' && customer) {
        setTicketPage(1); // Reset para primeira página ao pesquisar
        fetchTickets();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [ticketSearch, activeTab, customer, fetchTickets]);

  useEffect(() => {
    fetchCustomer();
    fetchAssets();
    fetchAllServiceDesks();
    fetchAvailableTags();
  }, [fetchCustomer, fetchAssets, fetchAllServiceDesks, fetchAvailableTags]);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedAssetSearchText(assetSearchText.trim()),
      300
    );
    return () => clearTimeout(t);
  }, [assetSearchText]);

  useEffect(() => {
    if (activeTab === 'requesters' && customer) {
      fetchRequesters();
    } else if (activeTab === 'tickets' && customer && !ticketSearch) {
      // Só carrega tickets automaticamente se não houver pesquisa ativa
      fetchTickets();
    }
  }, [activeTab, customer, fetchRequesters, fetchTickets, ticketSearch]);

  const handleEdit = (field) => {
    setEditingField(field);
  };

  const handleSave = async (field) => {
    try {
      let valueToSave = editValues[field];

      // Remove formatação do CPF/CNPJ antes de enviar para o backend
      if (field === 'cpfCnpj') {
        valueToSave = editValues[field].replace(/\D/g, '');
      }

      const updateData = { [field]: valueToSave };
      await api.put(`/customers/${id}`, updateData);

      setCustomer({ ...customer, [field]: editValues[field] }); // Mantém formatação na exibição
      setEditingField(null);
      setSuccess('Campo atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Erro ao atualizar campo ${field}`);
      console.error('err');
    }
  };

  const handleCancel = (field) => {
    setEditValues({ ...editValues, [field]: customer[field] });
    setEditingField(null);
  };

  // Função para formatar CPF (xxx.xxx.xxx-xx)
  const formatCPF = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      return cleanValue
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return cleanValue
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  // Função para formatar CNPJ (xx.xxx.xxx/xxxx-xx)
  const formatCNPJ = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 14) {
      return cleanValue
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    return cleanValue
      .slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  // Função para remover formatação (retorna apenas números)
  const removeFormatting = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    // Aplica formatação automática para CPF/CNPJ
    if (field === 'cpfCnpj') {
      if (customer.type_person === 'fisica') {
        formattedValue = formatCPF(value);
      } else if (customer.type_person === 'juridica') {
        formattedValue = formatCNPJ(value);
      }
    }

    setEditValues({ ...editValues, [field]: formattedValue });
  };

  const renderEditableField = (label, field, type = 'text') => {
    const isEditing = editingField === field;
    const value = isEditing ? editValues[field] : customer[field];

    // Usar RichTextEditor para o campo de informações do cliente
    if (field === 'notes') {
      return (
        <Row className="mb-3">
          <Col md={12}>
            <div className="mb-2">
              <strong>{label}:</strong>
            </div>
            <div className="position-relative">
              <RichTextEditor
                value={value || ''}
                onChange={(newValue) => handleInputChange(field, newValue)}
                onSave={(finalValue) => {
                  setEditValues((prev) => ({ ...prev, [field]: finalValue }));
                  handleSave(field);
                }}
                onCancel={() => handleCancel(field)}
                isEditing={isEditing}
                label={label}
                placeholder="Digite as informações do cliente aqui..."
                maxImages={10}
              />
              {!isEditing && (
                <div className="position-absolute top-0 end-0 mt-2 me-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(field)}
                    title="Editar"
                  >
                    <FaEdit />
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <Row className="mb-3">
        <Col md={3}>
          <strong>{label}:</strong>
        </Col>
        <Col md={7}>
          {isEditing ? (
            <Form.Control
              type={type}
              value={value || ''}
              onChange={(e) => handleInputChange(field, e.target.value)}
              autoFocus
            />
          ) : (
            <span>{value || '-'}</span>
          )}
        </Col>
        <Col md={2}>
          {isEditing ? (
            <div className="d-flex gap-1">
              <Button
                variant="success"
                size="sm"
                onClick={() => handleSave(field)}
                title="Salvar"
              >
                <FaSave />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleCancel(field)}
                title="Cancelar"
              >
                <FaTimes />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleEdit(field)}
              title="Editar"
            >
              <FaEdit />
            </Button>
          )}
        </Col>
      </Row>
    );
  };

  const handleRequesterEdit = (requesterId) => {
    const requester = requesters.find((r) => r.id === requesterId);
    setEditingRequester(requesterId);
    setRequesterEditData({
      name: requester.name,
      email: requester.email,
      telefone: requester.telefone || '',
      celular: requester.celular || '',
    });
  };

  const handleRequesterCancel = () => {
    setEditingRequester(null);
    setRequesterEditData({});
  };

  const handleRequesterSave = async (requesterId) => {
    try {
      setRequestersLoading(true);
      // Usar o endpoint correto para atualizar solicitantes
      await api.put(`/requesters/${requesterId}`, requesterEditData);

      // Atualizar a lista local
      setRequesters((prev) =>
        prev.map((r) =>
          r.id === requesterId ? { ...r, ...requesterEditData } : r
        )
      );

      setEditingRequester(null);
      setRequesterEditData({});
      setSuccess('Solicitante atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao atualizar solicitante:', err);
      setError('Erro ao atualizar solicitante', err.response.data.message);
    } finally {
      setRequestersLoading(false);
    }
  };

  const handleRequesterInputChange = (field, value) => {
    setRequesterEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const requestersColumns = [
    {
      name: 'Nome',
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => {
        const isEditing = editingRequester === row.id;
        return isEditing ? (
          <Form.Control
            type="text"
            value={requesterEditData.name || ''}
            onChange={(e) => handleRequesterInputChange('name', e.target.value)}
            size="sm"
          />
        ) : (
          <span>{row.name}</span>
        );
      },
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => {
        const isEditing = editingRequester === row.id;
        return isEditing ? (
          <Form.Control
            type="email"
            value={requesterEditData.email || ''}
            onChange={(e) =>
              handleRequesterInputChange('email', e.target.value)
            }
            size="sm"
          />
        ) : (
          <span>{row.email}</span>
        );
      },
    },
    {
      name: 'Telefone',
      selector: (row) => row.telefone,
      sortable: true,
      cell: (row) => {
        const isEditing = editingRequester === row.id;
        return isEditing ? (
          <Form.Control
            type="phone"
            value={requesterEditData.telefone || ''}
            onChange={(e) =>
              handleRequesterInputChange('telefone', e.target.value)
            }
            size="sm"
          />
        ) : (
          <span>{formatPhone(row.telefone)}</span>
        );
      },
    },
    {
      name: 'Celular',
      selector: (row) => row.celular,
      sortable: true,
      cell: (row) => {
        const isEditing = editingRequester === row.id;
        return isEditing ? (
          <Form.Control
            type="phone"
            value={requesterEditData.celular || ''}
            onChange={(e) =>
              handleRequesterInputChange('celular', e.target.value)
            }
            size="sm"
          />
        ) : (
          <span>{formatPhone(row.celular)}</span>
        );
      },
    },
    {
      name: 'Ações',
      cell: (row) => {
        const isEditing = editingRequester === row.id;
        return (
          <div className="d-flex gap-1">
            {isEditing ? (
              <>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleRequesterSave(row.id)}
                  title="Salvar"
                  disabled={requestersLoading}
                >
                  <FaSave />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRequesterCancel}
                  title="Cancelar"
                  disabled={requestersLoading}
                >
                  <FaTimes />
                </Button>
              </>
            ) : (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleRequesterEdit(row.id)}
                title="Editar solicitante"
                disabled={editingRequester !== null}
              >
                <FaEdit />
              </Button>
            )}
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '120px',
    },
  ];

  const ticketsColumns = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '180px',
      cell: (row) => (
        <Button
          variant="link"
          className="p-0"
          onClick={() => navigate(`/tickets/${row.id}/edit`)}
          title="Ver ticket"
        >
          #{row.id} <FaExternalLinkAlt size={12} />
        </Button>
      ),
    },
    {
      name: 'Título',
      selector: (row) => row.title,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <Button
          variant="link"
          className="p-0 text-start text-decoration-none"
          onClick={() => navigate(`/tickets/${row.id}/edit`)}
          style={{ whiteSpace: 'normal', textAlign: 'left' }}
        >
          {row.title}
        </Button>
      ),
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const statusColors = {
          aberto: 'success',
          em_andamento: 'warning',
          pausado: 'secondary',
          fechado: 'danger',
        };
        return (
          <Badge bg={statusColors[row.status] || 'secondary'}>
            {row.status}
          </Badge>
        );
      },
    },
    {
      name: 'Criado em',
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString('pt-BR'),
    },
  ];

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Cliente não encontrado</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Breadcrumbs
        items={[
          { label: 'Clientes', href: '/customers' },
          { label: customer.nomeEmpresa || customer.razaoSocial, active: true },
        ]}
      />

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              {customer.nomeEmpresa || customer.razaoSocial}
            </h4>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/customers')}
            >
              Voltar
            </Button>
          </div>
        </Card.Header>

        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Card.Header className="p-10">
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="info">
                  <FaInfoCircle className="me-2" />
                  Informações
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="requesters">
                  <FaUser className="me-2" />
                  Solicitantes
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tags">
                  <FaEdit className="me-2" />
                  Etiquetas
                  {selectedTags.length > 0 && (
                    <Badge bg="success" className="ms-2">
                      {selectedTags.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="servicedesk">
                  <FaExternalLinkAlt className="me-2" />
                  Mesa de Serviço
                  {serviceDeskIds.length > 0 && (
                    <Badge bg="success" className="ms-2">
                      {serviceDeskIds.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="insights">
                  <FaRobot className="me-2" />
                  Relatórios IA
                  {insightsAtivo && (
                    <Badge bg="success" className="ms-2">
                      Ativo
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tickets">
                  <FaTicketAlt className="me-2" />
                  Tickets
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="assets">
                  <FaInfoCircle className="me-2" />
                  Ativos
                  {assets.length > 0 && (
                    <Badge bg="secondary" className="ms-2">
                      {assets.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>

          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey="info">
                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Seção Informações Básicas */}
                  <div
                    style={{
                      backgroundColor: '#3b3fbd',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '0.25rem 0.25rem 0 0',
                      marginBottom: '0',
                    }}
                  >
                    <strong>Informações Básicas</strong>
                  </div>
                  <div
                    style={{
                      padding: '1.5rem',
                      border: '1px solid #e0e0e0',
                      borderTop: 'none',
                      borderRadius: '0 0 0.25rem 0.25rem',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        {renderEditableField('Nome fantasia', 'nomeEmpresa')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Razão social', 'razaoSocial')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('CPF/CNPJ', 'cpfCnpj')}
                      </Col>
                      <Col md={6}>
                        <Row className="mb-3">
                          <Col md={6}>
                            <strong>Tipo de Pessoa:</strong>
                          </Col>
                          <Col md={6}>
                            <span>
                              {customer.type_person === 'juridica'
                                ? 'Jurídica'
                                : customer.type_person === 'fisica'
                                  ? 'Física'
                                  : '-'}
                            </span>
                          </Col>
                        </Row>
                      </Col>
                      <Col md={6}>
                        <Row className="mb-3">
                          <Col md={6}>
                            <strong>Natureza Jurídica:</strong>
                          </Col>
                          <Col md={6}>
                            <span>{customer.naturezaJuridica || '-'}</span>
                          </Col>
                        </Row>
                      </Col>
                      <Col md={6}>
                        <Row className="mb-3">
                          <Col md={6}>
                            <strong>Situação Cadastral:</strong>
                          </Col>
                          <Col md={6}>
                            <span>{customer.situacaoCadastral || '-'}</span>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                  {/* Seção Endereço */}
                  <div
                    style={{
                      backgroundColor: '#3b3fbd',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '0.25rem 0.25rem 0 0',
                      marginBottom: '0',
                    }}
                  >
                    <strong>Endereço</strong>
                  </div>
                  <div
                    style={{
                      padding: '1.5rem',
                      border: '1px solid #e0e0e0',
                      borderTop: 'none',
                      borderRadius: '0 0 0.25rem 0.25rem',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <Row>
                      <Col md={6}>{renderEditableField('CEP', 'cep')}</Col>
                      <Col md={6}>
                        {renderEditableField('Logradouro', 'logradouro')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Número', 'numero')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Complemento', 'complemento')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Bairro', 'bairro')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Cidade', 'cidade')}
                      </Col>
                      <Col md={6}>{renderEditableField('País', 'pais')}</Col>
                      <Col md={6}>
                        {renderEditableField('Estado', 'estado')}
                      </Col>
                      <Col md={6}>
                        {renderEditableField('Telefone', 'telefone')}
                      </Col>
                    </Row>
                  </div>

                  {/* Seção Outras Informações */}
                  <div
                    style={{
                      backgroundColor: '#3b3fbd',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '0.25rem 0.25rem 0 0',
                      marginBottom: '0',
                    }}
                  >
                    <strong>Outras Informações</strong>
                  </div>
                  <div
                    style={{
                      padding: '1.5rem',
                      border: '1px solid #e0e0e0',
                      borderTop: 'none',
                      borderRadius: '0 0 0.25rem 0.25rem',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <Row>
                      <Col md={12}>
                        {renderEditableField(
                          'Informações do cliente',
                          'notes',
                          'textarea'
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/customers')}
                    >
                      Voltar
                    </Button>
                  </div>
                </form>
              </Tab.Pane>

              <Tab.Pane eventKey="requesters">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Solicitantes do Cliente</h5>
                  <Badge bg="secondary">{requesters.length} solicitantes</Badge>
                </div>

                {requestersLoading ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando solicitantes...</span>
                  </div>
                ) : (
                  <DataTable
                    columns={requestersColumns}
                    data={requesters}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50]}
                    noDataComponent="Nenhum solicitante encontrado"
                    responsive
                    striped
                    highlightOnHover
                  />
                )}
              </Tab.Pane>

              {/* Aba Etiquetas */}
              <Tab.Pane eventKey="tags">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>
                      <FaEdit className="me-2" />
                      Etiquetas do Cliente
                    </h5>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={saveTagAssociations}
                      disabled={tagsLoading}
                    >
                      {tagsLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Salvar Etiquetas
                        </>
                      )}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <label className="form-label">
                        <strong>Selecionar Etiquetas:</strong>
                      </label>
                      <TagSelector
                        availableTags={availableTags}
                        selectedTags={selectedTags}
                        onChange={setSelectedTags}
                        isMulti
                        placeholder="Selecione as etiquetas para este cliente..."
                        isLoading={tagsLoading}
                      />
                    </div>

                    {selectedTags.length > 0 && (
                      <div>
                        <label className="form-label">
                          <strong>Etiquetas Selecionadas:</strong>
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedTags.map((tag) => (
                            <TagBadge
                              key={tag.id}
                              tag={tag}
                              onRemove={(tagToRemove) => {
                                setSelectedTags(
                                  selectedTags.filter(
                                    (t) => t.id !== tagToRemove.id
                                  )
                                );
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedTags.length === 0 && (
                      <div className="text-center text-muted py-4">
                        <FaEdit size={48} className="mb-3 opacity-50" />
                        <p>Nenhuma etiqueta selecionada</p>
                        <small>
                          Use o seletor acima para adicionar etiquetas a este
                          cliente
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Aba Mesa de Serviço */}
              <Tab.Pane eventKey="servicedesk">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>
                      <FaExternalLinkAlt className="me-2" />
                      Mesa de Serviço
                    </h5>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={saveServiceDeskAssociations}
                      disabled={serviceDeskLoading}
                    >
                      {serviceDeskLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Salvar Associações
                        </>
                      )}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {serviceDeskLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">
                          Carregando mesas de serviço...
                        </span>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '0.75rem',
                        }}
                      >
                        {allServiceDesks.map((desk) => (
                          <label
                            key={desk.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '0.95rem',
                              padding: '0.5rem',
                              border: '1px solid #e0e0e0',
                              borderRadius: '0.25rem',
                              backgroundColor: serviceDeskIds.includes(desk.id)
                                ? '#e8f5e8'
                                : '#f8f9fa',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={serviceDeskIds.includes(desk.id)}
                              onChange={() => handleServiceDeskChange(desk.id)}
                              style={{ marginRight: '0.5rem' }}
                            />
                            <div>
                              <div>{desk.displayName}</div>
                              {desk.description && (
                                <small className="text-muted">
                                  {desk.description}
                                </small>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    {allServiceDesks.length === 0 && !serviceDeskLoading && (
                      <div className="text-center text-muted">
                        Nenhuma mesa de serviço disponível
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Aba Relatórios com IA */}
              <Tab.Pane eventKey="insights">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>
                      <FaRobot className="me-2" />
                      Relatórios Mensais com IA
                    </h5>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={saveInsightsConfig}
                      disabled={insightsLoading}
                    >
                      {insightsLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Salvar Configuração
                        </>
                      )}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {/* Toggle ativo/inativo */}
                    <div
                      style={{
                        backgroundColor: insightsAtivo ? '#6f42c1' : '#6c757d',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '0.375rem',
                        marginBottom: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <strong>Envio de Relatórios Mensais com IA</strong>
                        <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                          {insightsAtivo
                            ? 'O serviço de IA irá coletar dados e enviar relatórios mensais'
                            : 'Ative para configurar o envio de relatórios mensais'}
                        </div>
                      </div>
                      <Form.Check
                        type="switch"
                        id="insights-switch-edit"
                        checked={insightsAtivo}
                        onChange={() => setInsightsAtivo((prev) => !prev)}
                        label={insightsAtivo ? 'Ativo' : 'Inativo'}
                        style={{ color: 'white', marginBottom: 0 }}
                      />
                    </div>

                    {insightsAtivo && (
                      <Row>
                        {/* Produtos */}
                        <Col md={12} className="mb-4">
                          <label className="form-label fw-semibold">
                            Produtos
                          </label>
                          <div className="d-flex gap-4">
                            <Form.Check
                              type="checkbox"
                              id="edit-produto-telefonia"
                              label="Telefonia"
                              checked={insightsConfig.produtos.includes(
                                'telefonia'
                              )}
                              onChange={() =>
                                handleInsightsProdutoChange('telefonia')
                              }
                            />
                            <Form.Check
                              type="checkbox"
                              id="edit-produto-chatclinic"
                              label="ChatClinic"
                              checked={insightsConfig.produtos.includes(
                                'chatclinic'
                              )}
                              onChange={() =>
                                handleInsightsProdutoChange('chatclinic')
                              }
                            />
                          </div>
                        </Col>

                        {/* Telefones */}
                        <Col md={6} className="mb-4">
                          <label className="form-label fw-semibold">
                            Telefones para envio dos relatórios
                          </label>
                          {insightsConfig.telefones.map((tel, index) => (
                            <div key={index} className="d-flex gap-2 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Ex: 11999999999"
                                value={tel}
                                onChange={(e) =>
                                  handleTelefoneChange(index, e.target.value)
                                }
                              />
                              {insightsConfig.telefones.length > 1 && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeTelefone(index)}
                                >
                                  ✕
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={addTelefone}
                          >
                            + Adicionar telefone
                          </Button>
                        </Col>

                        {/* Servidor */}
                        <Col md={6} className="mb-4">
                          <label className="form-label fw-semibold">
                            Servidor (DNS ou IP Público)
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ex: meuservidor.com.br ou 200.100.50.10"
                            value={insightsConfig.servidor}
                            onChange={(e) =>
                              setInsightsConfig((prev) => ({
                                ...prev,
                                servidor: e.target.value,
                              }))
                            }
                          />
                          <small className="text-muted">
                            Apenas DNS ou IP público. IPs locais não são
                            suportados.
                          </small>
                        </Col>

                        {/* Token ChatClinic */}
                        {insightsConfig.produtos.includes('chatclinic') && (
                          <Col md={6} className="mb-4">
                            <label className="form-label fw-semibold">
                              Token ChatClinic
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Token da API do ChatClinic"
                              value={insightsConfig.tokenChat}
                              onChange={(e) =>
                                setInsightsConfig((prev) => ({
                                  ...prev,
                                  tokenChat: e.target.value,
                                }))
                              }
                            />
                          </Col>
                        )}

                        {/* Token Telefonia */}
                        {insightsConfig.produtos.includes('telefonia') && (
                          <Col md={6} className="mb-4">
                            <label className="form-label fw-semibold">
                              Token Telefonia
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Token da API da Telefonia"
                              value={insightsConfig.tokenTelefonia}
                              onChange={(e) =>
                                setInsightsConfig((prev) => ({
                                  ...prev,
                                  tokenTelefonia: e.target.value,
                                }))
                              }
                            />
                          </Col>
                        )}
                      </Row>
                    )}

                    {!insightsAtivo && (
                      <div className="text-center text-muted py-4">
                        <FaRobot size={48} className="mb-3 opacity-50" />
                        <p>
                          Relatórios Mensais com IA estão desativados para este
                          cliente
                        </p>
                        <small>Ative o toggle acima para configurar</small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="tickets">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Tickets do Cliente</h5>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type="text"
                      placeholder="Buscar tickets..."
                      value={ticketSearch}
                      onChange={(e) => setTicketSearch(e.target.value)}
                      style={{ width: '250px' }}
                    />
                    <Badge bg="secondary">{tickets.length} tickets</Badge>
                  </div>
                </div>

                {ticketsLoading ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando tickets...</span>
                  </div>
                ) : (
                  <>
                    <DataTable
                      columns={ticketsColumns}
                      data={tickets}
                      pagination
                      paginationPerPage={50}
                      paginationTotalRows={ticketTotalPages * 50}
                      paginationServer
                      onChangePage={(page) => setTicketPage(page)}
                      noDataComponent="Nenhum ticket encontrado"
                      responsive
                      striped
                      highlightOnHover
                    />

                    {ticketTotalPages > 1 && (
                      <div className="mt-3 text-center">
                        <small className="text-muted">
                          Página {ticketPage} de {ticketTotalPages} (50 tickets
                          por página)
                        </small>
                      </div>
                    )}
                  </>
                )}
              </Tab.Pane>
              <Tab.Pane eventKey="assets">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>Ativos do Cliente</h5>
                    <Badge bg="secondary">{assets.length} ativos</Badge>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={6} lg={5} xl={4}>
                        <Form.Group controlId="assetSearch">
                          <Form.Label>Pesquisar ativo</Form.Label>
                          <Form.Control
                            type="search"
                            placeholder="Buscar por qualquer campo do ativo..."
                            value={assetSearchText}
                            onChange={(e) => setAssetSearchText(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <table className="table table-striped table-bordered table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Categoria</th>
                            <th>IP</th>
                            <th>Fabricante</th>
                            <th>Modelo</th>
                            <th>Nº Série</th>
                            <th>Hostname</th>
                            <th>SO</th>
                            <th>Especificações</th>
                            <th>Data Aquisição</th>
                            <th>Unidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(debouncedAssetSearchText
                            ? assets.filter((a) => {
                                const hay = Object.entries(a)
                                  .filter(([k]) => k !== 'id')
                                  .map(([, v]) => String(v).toLowerCase())
                                  .join(' ');
                                return hay.includes(
                                  debouncedAssetSearchText.toLowerCase()
                                );
                              })
                            : assets
                          ).length === 0 ? (
                            <tr>
                              <td
                                colSpan={10}
                                className="text-center text-muted py-3"
                              >
                                Nenhum ativo encontrado
                              </td>
                            </tr>
                          ) : (
                            (debouncedAssetSearchText
                              ? assets.filter((a) => {
                                  const hay = Object.entries(a)
                                    .filter(([k]) => k !== 'id')
                                    .map(([, v]) => String(v).toLowerCase())
                                    .join(' ');
                                  return hay.includes(
                                    debouncedAssetSearchText.toLowerCase()
                                  );
                                })
                              : assets
                            ).map((a) => (
                              <tr key={a.id}>
                                <td>
                                  <Badge bg="info">{a.category}</Badge>
                                </td>
                                <td>
                                  <span>{a.ip || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.fabricante || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.modelo || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.numeroSerie || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.hostname || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.so || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.especificacoes || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.dataAquisicao || '-'}</span>
                                </td>
                                <td>
                                  <span>{a.unidade || '-'}</span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Tab.Container>
      </Card>
    </Container>
  );
}

export default CustomerEdit;
